import {
    Box,
    Container,
    Button,
    Typography,
    Card,
    CircularProgress,
    TextField,
    Alert,
    InputAdornment,
    Stack
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useForgotPassword } from '../../react-query/auth-queries';
import { useState } from 'react';
import { ROUTES } from '../../router/routes';
import { brand } from '../../shared-theme/themePrimitives';
import { Email } from '@mui/icons-material';
import { PodcastIcon } from '@/components/icons/PodcastIcon';

export default function ForgotPasswordPage() {
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const { mutateAsync: forgotPassword, isPending } = useForgotPassword();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setMessage('');
        setError('');
        try {
            const response = await forgotPassword(email);
            if (response.success) {
                setMessage('If an account exists with this email, you will receive a password reset link.');
            } else {
                setError(response.message || 'Request failed');
            }
        } catch (err: any) {
            console.error("Forgot password error:", err);
            setError(err.response?.data?.message || 'An error occurred');
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'radial-gradient(ellipse at 50% -20%, rgba(210, 225, 255, 0.5) 0%, rgba(255,255,255,0) 70%)',
                bgcolor: '#FAFAFA',
                py: 4,
                px: 2
            }}
        >
            <Container maxWidth="sm" >
                <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box sx={{ mb: 3 }}>
                        <PodcastIcon size={48} color={brand[500]} />
                    </Box>
                    <Typography variant="h3" component="h1" gutterBottom fontWeight={800} sx={{ color: '#0F172A', fontSize: { xs: '1.75rem', md: '2rem' } }}>
                        Forgot password?
                    </Typography>
                    <Typography variant="body1" color="text.secondary" textAlign="center" maxWidth="80%">
                        No worries, we'll send you reset instructions.
                    </Typography>
                </Box>

                <Card 
                    elevation={0} 
                    sx={{ 
                        p: { xs: 3, sm: 5 }, 
                        borderRadius: '24px',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: '0 10px 40px -10px rgba(0,0,0,0.05)'
                    }}
                >
                    {message && <Alert severity="success" sx={{ mb: 3, borderRadius: '12px' }}>{message}</Alert>}
                    {error && <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>{error}</Alert>}
                    
                    <Box component="form" onSubmit={handleSubmit} noValidate>
                        <Stack spacing={2.5}>
                            <Box>
                                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.75, color: '#334155' }}>
                                    Email Address
                                </Typography>
                                <TextField
                                    required
                                    fullWidth
                                    id="email"
                                    name="email"
                                    placeholder="john@company.com"
                                    autoComplete="email"
                                    autoFocus
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email sx={{ color: 'text.disabled', fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { bgcolor: 'white' } }}
                                />
                            </Box>

                            <Button
                                fullWidth
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={isPending}
                                sx={{ 
                                    py: 1.5,
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    bgcolor: brand[400],
                                    '&:hover': { bgcolor: brand[500] },
                                    boxShadow: '0 10px 20px -10px rgba(225, 89, 27, 0.4)'
                                }}
                            >
                                {isPending ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
                            </Button>
                            
                             <Button
                                fullWidth
                                variant="text"
                                onClick={() => navigate(ROUTES.LOGIN.path)}
                                sx={{ 
                                    fontWeight: 600, 
                                    color: 'text.secondary',
                                    '&:hover': { color: '#0F172A', bgcolor: 'transparent' }
                                }}
                            >
                                ← Back to login
                            </Button>
                        </Stack>
                    </Box>
                </Card>
            </Container>
        </Box>
    );
}
